/**
 * BeatShelf Component
 *
 * A collapsible horizontal strip for storing beats that are excluded from the timeline.
 * Displays compact versions of beat nodes (name only) in a scrollable row.
 */

import { CSSProperties, useState, useCallback, useMemo } from 'react';
import { useProjectStore } from '@/stores/projectStore';
import { useFocusedNodeId } from '@/stores/playbackStore';

function BeatShelf() {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const nodes = useProjectStore((state) => state.nodes);
  const selectNode = useProjectStore((state) => state.selectNode);
  const selectedNodeIds = useProjectStore((state) => state.selectedNodeIds);
  const updateNode = useProjectStore((state) => state.updateNode);
  const updateNodePosition = useProjectStore((state) => state.updateNodePosition);
  const saveHistory = useProjectStore((state) => state.saveHistory);
  const focusedNodeId = useFocusedNodeId();

  // Filter nodes that are on the shelf
  const shelfNodes = useMemo(() => {
    return nodes.filter((node) => node.data.onShelf === true);
  }, [nodes]);

  /**
   * Toggle node between shelf and main view
   */
  const handleNodeClick = useCallback(
    (nodeId: string) => {
      selectNode(nodeId);
    },
    [selectNode]
  );

  /**
   * Calculate position to place node after current beat in timeline
   * Also shifts any nodes that would be in the way to the right
   */
  const getPositionAfterCurrentBeat = useCallback((nodeIdToPlace: string) => {
    const SPACING = 200; // Space between nodes

    // Get all active (non-shelf) nodes excluding the one we're placing
    const activeNodes = nodes.filter((n) => !n.data.onShelf && n.id !== nodeIdToPlace);

    // Sort nodes by x position to get timeline order
    const sortedNodes = [...activeNodes].sort((a, b) => a.position.x - b.position.x);

    let targetX = 0;
    let targetY = 0;

    // If there's a focused node, we want to insert after it
    if (focusedNodeId) {
      const focusedIndex = sortedNodes.findIndex((n) => n.id === focusedNodeId);

      if (focusedIndex !== -1) {
        const focusedNode = sortedNodes[focusedIndex];
        targetX = focusedNode.position.x + SPACING;
        targetY = focusedNode.position.y;

        // Shift all nodes after the focused node to make room
        for (let i = focusedIndex + 1; i < sortedNodes.length; i++) {
          const currentNode = sortedNodes[i];
          const expectedMinX = targetX + (i - focusedIndex) * SPACING;

          // If this node is too close, shift it and all subsequent nodes
          if (currentNode.position.x < expectedMinX) {
            updateNodePosition(currentNode.id, {
              x: expectedMinX,
              y: currentNode.position.y,
            });
          }
        }

        return { x: targetX, y: targetY };
      }
    }

    // Fallback: If no focused node or focused node not found, place at the end
    if (sortedNodes.length > 0) {
      const lastNode = sortedNodes[sortedNodes.length - 1];
      return {
        x: lastNode.position.x + SPACING,
        y: lastNode.position.y,
      };
    }

    // Ultimate fallback: Default position if no nodes exist
    return { x: 0, y: 0 };
  }, [nodes, focusedNodeId, updateNodePosition]);

  /**
   * Handle double-click to restore node from shelf
   */
  const handleNodeDoubleClick = useCallback(
    (nodeId: string) => {
      saveHistory();
      // Calculate position after current beat (passing nodeId to exclude it from collision check)
      const newPosition = getPositionAfterCurrentBeat(nodeId);

      // Move node from shelf and place it after current beat
      updateNode(nodeId, { onShelf: false, shelfStoredPosition: undefined });
      updateNodePosition(nodeId, newPosition);
    },
    [getPositionAfterCurrentBeat, updateNode, updateNodePosition, saveHistory]
  );

  /**
   * Move node from shelf to main view
   */
  const handleMoveToMain = useCallback(
    (nodeId: string, event: React.MouseEvent) => {
      event.stopPropagation();

      saveHistory();
      // Calculate position after current beat (passing nodeId to exclude it from collision check)
      const newPosition = getPositionAfterCurrentBeat(nodeId);

      // Move node from shelf and place it after current beat
      updateNode(nodeId, { onShelf: false, shelfStoredPosition: undefined });
      updateNodePosition(nodeId, newPosition);
    },
    [getPositionAfterCurrentBeat, updateNode, updateNodePosition, saveHistory]
  );

  // ---------------------------------------------------------------------------
  // STYLES
  // ---------------------------------------------------------------------------

  const containerStyles: CSSProperties = {
    gridArea: 'shelf',
    backgroundColor: 'var(--nb-bg-tertiary)',
    borderTop: '1px solid var(--nb-border-primary)',
    borderBottom: '1px solid var(--nb-border-primary)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    transition: 'height var(--nb-transition-normal)',
  };

  const headerStyles: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--nb-space-sm)',
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: 'var(--nb-bg-secondary)',
    borderBottom: '1px solid var(--nb-border-primary)',
    fontSize: 'var(--nb-font-size-xs)',
    color: 'var(--nb-text-secondary)',
    userSelect: 'none',
  };

  const toggleButtonStyles: CSSProperties = {
    background: 'none',
    border: 'none',
    color: 'var(--nb-text-secondary)',
    cursor: 'pointer',
    padding: '2px',
    fontSize: 'var(--nb-font-size-sm)',
    lineHeight: 1,
    display: 'flex',
    alignItems: 'center',
  };

  const contentStyles: CSSProperties = {
    display: isCollapsed ? 'none' : 'flex',
    gap: 'var(--nb-space-sm)',
    padding: 'var(--nb-space-sm)',
    overflowX: 'auto',
    overflowY: 'hidden',
    alignItems: 'center',
    flex: 1,
  };

  const shelfNodeStyles = (isSelected: boolean, customColor?: string): CSSProperties => ({
    minWidth: 120,
    maxWidth: 180,
    height: 32,
    padding: 'var(--nb-space-xs) var(--nb-space-sm)',
    backgroundColor: customColor || (isSelected ? 'var(--nb-bg-active)' : 'var(--nb-bg-secondary)'),
    border: `1px solid ${isSelected ? 'var(--nb-node-selected)' : 'var(--nb-border-primary)'}`,
    borderRadius: 'var(--nb-radius-sm)',
    cursor: 'pointer',
    fontSize: 'var(--nb-font-size-xs)',
    color: 'var(--nb-text-primary)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--nb-space-xs)',
    transition: 'background-color var(--nb-transition-fast), border-color var(--nb-transition-fast)',
  });

  const moveButtonStyles: CSSProperties = {
    marginLeft: 'auto',
    background: 'none',
    border: 'none',
    color: 'var(--nb-text-muted)',
    cursor: 'pointer',
    padding: '2px',
    fontSize: '10px',
    lineHeight: 1,
  };

  const emptyStateStyles: CSSProperties = {
    color: 'var(--nb-text-muted)',
    fontSize: 'var(--nb-font-size-xs)',
    fontStyle: 'italic',
  };

  return (
    <div style={containerStyles}>
      <div style={headerStyles}>
        <button
          style={toggleButtonStyles}
          onClick={() => setIsCollapsed(!isCollapsed)}
          title={isCollapsed ? 'Expand shelf' : 'Collapse shelf'}
        >
          {isCollapsed ? '▶' : '▼'}
        </button>
        <span>Beat Shelf ({shelfNodes.length})</span>
      </div>

      <div style={contentStyles}>
        {shelfNodes.length === 0 ? (
          <div style={emptyStateStyles}>
            Double-click a beat node to move it to the shelf
          </div>
        ) : (
          shelfNodes.map((node) => (
            <div
              key={node.id}
              style={shelfNodeStyles(selectedNodeIds.includes(node.id), node.data.color)}
              onClick={() => handleNodeClick(node.id)}
              onDoubleClick={() => handleNodeDoubleClick(node.id)}
              title={`${node.data.title} (double-click to restore)`}
            >
              <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {node.data.title || 'Untitled'}
              </span>
              <button
                style={moveButtonStyles}
                onClick={(e) => handleMoveToMain(node.id, e)}
                title="Move to main view"
              >
                ↓
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default BeatShelf;
